import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useState } from 'react';

import { makeAuthenticatedApiCall } from '../../apiHelper';
import legacyTheme from '../../theme';
import { tokens } from '../../theme-next';

const DOC_CHECKLIST: Array<{ docType: string; label: string }> = [
  { docType: 'carte_grise', label: 'Carte grise' },
  { docType: 'cerfa_cession', label: 'Certificat de cession (Cerfa 15776*01)' },
  { docType: 'cerfa_demande_immat', label: "Cerfa demande d'immatriculation (13750*05)" },
  { docType: 'mandat', label: "Mandat d'immatriculation au nom de Skipcar" },
  { docType: 'assurance', label: "Attestation d'assurance" },
  { docType: 'controle_technique', label: 'Contrôle technique' },
  { docType: 'piece_identite', label: "Pièce d'identité du nouveau titulaire" },
  { docType: 'permis_conduire', label: 'Permis de conduire' },
  { docType: 'kbis_acheteur', label: "Extrait Kbis de l'acquéreur" },
  { docType: 'kbis_vendeur', label: 'Extrait Kbis du vendeur' },
  { docType: 'justif_domicile', label: 'Justificatif de domicile' },
  { docType: 'attestation_hebergement', label: "Attestation d'hébergement" },
  // Récépissé de déclaration d'achat (Parcours A). Disponible toujours dans
  // la checklist — le GOD coche manuellement s'il a détecté un cédant pro
  // (SIRET côté cession + C.1 carte grise particulier). Cf. règles regex
  // parcours_a_requis / recepisse_da_manquant_parcours_a côté frontend.
  { docType: 'recepisse_da', label: "Récépissé de déclaration d'achat (Parcours A)" },
];

interface RejectCGModalProps {
  open: boolean;
  registrationId: number;
  regPlate: string | null;
  onClose: () => void;
  onRejected: (newStatus: string) => void;
}

interface GenerateMessageResponse {
  success: boolean;
  data?: { introText: string; items: Array<{ docType: string; label: string }> };
  error?: string;
}

interface RejectResponse {
  success: boolean;
  cgReviewStatus?: string;
  error?: string;
}

export const RejectCGModal = ({ open, registrationId, regPlate, onClose, onRejected }: RejectCGModalProps) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');
  const [introText, setIntroText] = useState<string>('');
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (docType: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(docType)) next.delete(docType);
      else next.add(docType);
      return next;
    });
    setIntroText('');
  };

  const handleGenerate = async () => {
    // On peut générer un message sans pièce cochée, à condition d'avoir un
    // contexte à donner à l'IA (sinon rien à rédiger).
    if (selected.size === 0 && !notes.trim()) return;
    setGenerating(true);
    setError(null);
    try {
      const response = await makeAuthenticatedApiCall<GenerateMessageResponse>(
        'post',
        `/api/cg/${registrationId}/generate-rejection-message`,
        { docTypes: Array.from(selected), notes: notes || undefined },
      );
      if (!response.data?.success || !response.data?.data) {
        throw new Error(response.data?.error || 'Erreur génération');
      }
      setIntroText(response.data.data.introText);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Erreur lors de la génération du message');
    } finally {
      setGenerating(false);
    }
  };

  /**
   * `expectsReply` n'a de sens que sans pièce cochée : il distingue les deux
   * intentions que l'envoi libre confondait. « Attend une réponse » crée une
   * demande de pièce complémentaire et sort la CG de la file de review ; le
   * simple message la laisse où elle est.
   *
   * Sans cette distinction, un « merci, c'est bon » basculait le dossier en
   * « Pièces à déposer » sans qu'aucune pièce ne soit demandée — et plus rien
   * ne l'en faisait sortir (retour François du 17/08/2026).
   */
  const handleSubmit = async (expectsReply: boolean) => {
    // Envoi possible sans pièce cochée (simple message à l'agence) : seul le
    // message est requis. Demandé par François.
    if (!introText.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await makeAuthenticatedApiCall<RejectResponse>(
        'post',
        `/api/cg/${registrationId}/reject`,
        {
          docTypes: Array.from(selected),
          introText: introText.trim(),
          notes: notes || undefined,
          expectsReply: selected.size > 0 ? true : expectsReply,
        },
      );
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Erreur rejet');
      }
      onRejected(response.data.cgReviewStatus ?? 'awaiting_docs');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Erreur lors du rejet');
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setSelected(new Set());
    setNotes('');
    setIntroText('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '20px' }}>
        Demander des pièces complémentaires {regPlate ? `· ${regPlate}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: tokens.color.dark, marginBottom: '8px' }}>
              Pièces à redemander à l'agence
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: '4px 16px',
                border: `1px solid ${tokens.color.border}`,
                borderRadius: `${tokens.radius.lg}px`,
                padding: '12px 16px',
              }}
            >
              {DOC_CHECKLIST.map((item) => (
                <FormControlLabel
                  key={item.docType}
                  control={
                    <Checkbox
                      checked={selected.has(item.docType)}
                      onChange={() => toggle(item.docType)}
                      size="small"
                    />
                  }
                  label={<Typography sx={{ fontSize: '13px' }}>{item.label}</Typography>}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: tokens.color.dark, marginBottom: '8px' }}>
              Contexte additionnel (optionnel)
            </Typography>
            <TextField
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setIntroText('');
              }}
              fullWidth
              multiline
              rows={3}
              placeholder="Ex : la carte grise déposée n'est pas signée par le co-titulaire."
            />
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ marginBottom: '8px' }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: tokens.color.dark }}>
                Message envoyé à l'agence
              </Typography>
              <Button
                size="small"
                onClick={() => void handleGenerate()}
                disabled={(selected.size === 0 && !notes.trim()) || generating || submitting}
                startIcon={generating ? <CircularProgress size={14} thickness={5} /> : undefined}
              >
                {generating ? 'Génération…' : introText ? 'Régénérer (IA)' : 'Générer avec IA'}
              </Button>
            </Stack>
            <TextField
              value={introText}
              onChange={(e) => setIntroText(e.target.value)}
              fullWidth
              multiline
              rows={5}
              placeholder="Cliquez sur « Générer avec IA » ou rédigez le message manuellement."
              helperText="Modifiable avant envoi. La liste des pièces sera ajoutée automatiquement en bas du mail."
            />
          </Box>

          {selected.size === 0 && introText.trim().length > 0 && (
            <Typography sx={{ fontSize: '13px', color: tokens.color.textMuted, lineHeight: 1.5 }}>
              Aucune pièce cochée. <strong>Attendre une réponse</strong> place le dossier en « Pièces à déposer »
              côté agence, avec votre message comme demande. <strong>Sans attendre de réponse</strong> envoie le
              message et laisse le dossier en review.
            </Typography>
          )}

          {error && (
            <Typography sx={{ fontSize: '13px', color: tokens.color.red, fontWeight: 600 }}>{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px' }}>
        <ThemeProvider theme={legacyTheme}>
          <Button className="secondaryDealActionButton" onClick={handleClose} disabled={submitting}>
            Annuler
          </Button>
          {selected.size === 0 && (
            <Button
              className="secondaryDealActionButton"
              onClick={() => void handleSubmit(false)}
              disabled={!introText.trim() || submitting || generating}
            >
              {submitting ? 'Envoi…' : 'Envoyer sans attendre de réponse'}
            </Button>
          )}
          <Button
            className="dealActionButton"
            onClick={() => void handleSubmit(true)}
            disabled={!introText.trim() || submitting || generating}
            color="primary"
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} thickness={5} color="inherit" /> : undefined}
          >
            {submitting
              ? 'Envoi…'
              : selected.size === 0
                ? 'Envoyer et attendre une réponse'
                : `Renvoyer à l'agence (${selected.size} pièce${selected.size > 1 ? 's' : ''})`}
          </Button>
        </ThemeProvider>
      </DialogActions>
    </Dialog>
  );
};
